export enum ThemeNames {
  AUDI = "AUDI",
  ADMIN = "ADMIN",
  BNP = "BNP",
  BNU = "BNU",
  BNU_ADMIN = "BNU_ADMIN",
  CONDOR = "CONDOR",
  HOERMANN = "HOERMANN",
  MAUSER = "MAUSER",
  NAHSH = "NAHSH",
  TEST = "TEST",
  WSM = "WSM",
}

export interface LegacyThemeLike {
  name: ThemeNames;
  font: string;
  loginBackground: string;
  vuetifyLightTheme: Record<string, unknown>;
  vuetifyDarkTheme: Record<string, unknown>;
}

export interface BrandTypography {
  fontFamily: string;
}

export interface BrandAssets {
  loginBackground: string;
}

export interface BrandModeColors {
  primary: string;
  secondary: string;
  accent: string;
  success: string;
  info: string;
  warning: string;
  error: string;
  surface: string;
  background: string;
}

export interface BrandThemeMode {
  colors: BrandModeColors;
  customColors: Record<string, string>;
}

export interface BrandNavbarTheme {
  background: string;
}

export interface BrandTheme {
  kind: "brand-theme";
  name: ThemeNames;
  typography: BrandTypography;
  assets: BrandAssets;
  density: "compact" | "comfortable" | "default";
  layout: {
    navbar: {
      light: BrandNavbarTheme;
      dark: BrandNavbarTheme;
    };
  };
  status: Record<string, string>;
  components: {
    button: {
      variant: "elevated" | "flat" | "outlined" | "plain" | "text" | "tonal";
    };
    card: {
      rounded: string;
    };
    field: {
      variant: "filled" | "outlined" | "plain" | "solo" | "underlined";
    };
  };
  modes: {
    light: BrandThemeMode;
    dark: BrandThemeMode;
  };
}

export type AppTheme = BrandTheme | LegacyThemeLike;

export interface VuetifyRuntimeTheme {
  dark: boolean;
  colors: Record<string, string>;
  variables: Record<string, string>;
}

export interface VuetifyThemeDefinition {
  defaultTheme: "dark" | "light";
  themes: {
    light: VuetifyRuntimeTheme;
    dark: VuetifyRuntimeTheme;
  };
  defaults: {
    VAppBar: {
      color: string;
    };
    VBtn: {
      variant: BrandTheme["components"]["button"]["variant"];
    };
    VCard: {
      rounded: string;
    };
    VTextField: {
      density: BrandTheme["density"];
      variant: BrandTheme["components"]["field"]["variant"];
    };
    VSelect: {
      density: BrandTheme["density"];
      variant: BrandTheme["components"]["field"]["variant"];
    };
  };
}

const DEFAULT_SURFACE = "#FFFFFF";
const DEFAULT_BACKGROUND = "#FFFFFF";

function toThemeString(value: unknown) {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  return undefined;
}

function pickColor(source: Record<string, unknown>, key: string, fallback: string) {
  const value = source[key];
  return toThemeString(value) ?? fallback;
}

function omitKeys(source: Record<string, unknown>, keys: string[]) {
  return Object.entries(source).reduce<Record<string, string>>((result, [key, value]) => {
    const stringValue = toThemeString(value);

    if (!keys.includes(key) && stringValue) {
      result[key] = stringValue;
    }

    return result;
  }, {});
}

function extractStatusColors(theme: Record<string, unknown>) {
  return Object.entries(theme).reduce<Record<string, string>>((result, [key, value]) => {
    const stringValue = toThemeString(value);

    if (key.startsWith("status-") && stringValue) {
      result[key.replace("status-", "")] = stringValue;
    }

    return result;
  }, {});
}

function createMode(theme: Record<string, unknown>): BrandThemeMode {
  const colors: BrandModeColors = {
    primary: pickColor(theme, "primary", "#000000"),
    secondary: pickColor(theme, "secondary", pickColor(theme, "accent", "#000000")),
    accent: pickColor(theme, "accent", pickColor(theme, "secondary", "#000000")),
    success: pickColor(theme, "success", pickColor(theme, "green", "#2e7d32")),
    info: pickColor(theme, "info", pickColor(theme, "primary", "#1976d2")),
    warning: pickColor(theme, "warning", "#fb8c00"),
    error: pickColor(theme, "error", pickColor(theme, "red", "#d32f2f")),
    surface: pickColor(theme, "surface", DEFAULT_SURFACE),
    background: pickColor(theme, "background", pickColor(theme, "surface", DEFAULT_BACKGROUND)),
  };

  const customColors = omitKeys(theme, [
    "primary",
    "secondary",
    "accent",
    "success",
    "info",
    "warning",
    "error",
    "surface",
    "background",
  ]);

  return {
    colors,
    customColors,
  };
}

export function isBrandTheme(theme: AppTheme | null | undefined): theme is BrandTheme {
  return Boolean(theme && "kind" in theme && theme.kind === "brand-theme");
}

export function adaptLegacyTheme(theme: LegacyThemeLike): BrandTheme {
  const lightMode = createMode(theme.vuetifyLightTheme);
  const darkMode = createMode(theme.vuetifyDarkTheme);

  return {
    kind: "brand-theme",
    name: theme.name,
    typography: {
      fontFamily: theme.font,
    },
    assets: {
      loginBackground: theme.loginBackground,
    },
    density: "default",
    layout: {
      navbar: {
        light: {
          background: pickColor(theme.vuetifyLightTheme, "navbar", lightMode.colors.primary),
        },
        dark: {
          background: pickColor(theme.vuetifyDarkTheme, "navbar", darkMode.colors.primary),
        },
      },
    },
    status: extractStatusColors(theme.vuetifyLightTheme),
    components: {
      button: {
        variant: "flat",
      },
      card: {
        rounded: "xl",
      },
      field: {
        variant: "outlined",
      },
    },
    modes: {
      light: lightMode,
      dark: darkMode,
    },
  };
}

export function resolveBrandTheme(theme: AppTheme): BrandTheme {
  if (isBrandTheme(theme)) {
    return theme;
  }

  return adaptLegacyTheme(theme);
}

function createRuntimeTheme(theme: BrandTheme, mode: "dark" | "light"): VuetifyRuntimeTheme {
  const modeTheme = theme.modes[mode];
  const navbar = theme.layout.navbar[mode].background;
  const statusColors = Object.entries(theme.status).reduce<Record<string, string>>(
    (result, [key, value]) => {
      result[`status-${key}`] = value;
      return result;
    },
    {},
  );

  return {
    dark: mode === "dark",
    colors: {
      ...modeTheme.colors,
      ...modeTheme.customColors,
      ...statusColors,
      navbar,
    },
    variables: {
      "brand-font-family": theme.typography.fontFamily,
      "login-background": theme.assets.loginBackground,
    },
  };
}

export function createVuetifyThemeDefinition(theme: AppTheme): VuetifyThemeDefinition {
  const resolvedTheme = resolveBrandTheme(theme);

  return {
    defaultTheme: "light",
    themes: {
      light: createRuntimeTheme(resolvedTheme, "light"),
      dark: createRuntimeTheme(resolvedTheme, "dark"),
    },
    defaults: {
      VAppBar: {
        color: "navbar",
      },
      VBtn: {
        variant: resolvedTheme.components.button.variant,
      },
      VCard: {
        rounded: resolvedTheme.components.card.rounded,
      },
      VTextField: {
        density: resolvedTheme.density,
        variant: resolvedTheme.components.field.variant,
      },
      VSelect: {
        density: resolvedTheme.density,
        variant: resolvedTheme.components.field.variant,
      },
    },
  };
}
